// Shared social-share utilities. Two responsibilities:
//   1. Build a natural-language sharing message that includes the universal
//      VOIHU "I'm passing this message to you" copy + referral URL.
//   2. Open platform-specific share URLs SYNCHRONOUSLY from the click handler,
//      so mobile Safari and popup blockers do not drop the intent when we
//      also fire analytics.
//
// Referral-URL contract: callers always pass a URL that already contains
// `?ref=…`. This module never rewrites URLs.
import { toast } from "sonner";

// Universal VOIHU share message. Deliberately generic — describes the human
// action (passing a MESSAGE), NOT the subject/cause of the MESSAGE itself.
// The current MESSAGE title is NOT embedded here (link previews already carry
// the title via OG metadata on the destination page).
const VOIHU_SHARE_LINES = [
  "I'm passing this message to you.",
  "Read it. If it speaks to you, add your voice and pass it on.",
  "One message. Many voices. One humanity.",
];

// Full form used by every channel that accepts prefilled text (WhatsApp,
// Telegram, Email, SMS, Instagram fallback, native share). Twitter/X gets a
// compact variant that still respects the 280-char envelope with the URL.
export function buildShareText(_messageTitle, { compact = false } = {}) {
  if (compact) {
    return "I'm passing this message to you. Add your voice — one message, many voices, one humanity.";
  }
  return VOIHU_SHARE_LINES.join("\n");
}

// URL builders — each returns a URL string ready for window.open.
// Facebook and LinkedIn ignore prefilled text server-side but respect the
// destination page's OG title/description in their own preview cards.
export const CHANNEL_URLS = {
  whatsapp: (url, text) =>
    `https://wa.me/?text=${encodeURIComponent(text + "\n\n" + url)}`,
  telegram: (url, text) =>
    `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  facebook: (url) =>
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  twitter: (url, text) =>
    `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  linkedin: (url) =>
    `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
  email: (url, text) =>
    `mailto:?subject=${encodeURIComponent("A MESSAGE for you — VOIHU")}&body=${encodeURIComponent(text + "\n\n" + url)}`,
  sms: (url, text) =>
    `sms:?&body=${encodeURIComponent(text + "\n\n" + url)}`,
};

/**
 * Open a share URL synchronously so the click's user-gesture is preserved.
 * Any side-effects (analytics ping) must be scheduled AFTER window.open.
 * Returns true when a window/tab was opened, false when it was blocked/skipped.
 */
export function openShareUrl(url) {
  if (!url) return false;
  const w = window.open(url, "_blank", "noopener,noreferrer");
  if (!w) {
    // Popup blocked. Fall back to same-tab navigation only for mailto/sms
    // (safe schemes that don't lose the page). Otherwise, let the caller decide.
    if (/^(mailto:|sms:)/.test(url)) {
      window.location.href = url;
      return true;
    }
    return false;
  }
  return true;
}

/**
 * True on desktop-class browsers (no Web-Share targeting Instagram sensibly,
 * no mobile OS share sheet). Used to pick the Instagram flow.
 */
export function isMobileUA() {
  return typeof navigator !== "undefined"
    && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
}

/**
 * Robust clipboard write:
 *  1. navigator.clipboard.writeText (modern, requires HTTPS/user-gesture)
 *  2. document.execCommand("copy") on a hidden textarea (works on legacy
 *     browsers, http contexts, and when clipboard-write permission was denied)
 * Returns true on success, false otherwise.
 */
export async function robustCopy(text) {
  const s = String(text ?? "");
  // Modern path
  try {
    if (typeof navigator !== "undefined"
        && navigator.clipboard
        && typeof navigator.clipboard.writeText === "function") {
      await navigator.clipboard.writeText(s);
      return true;
    }
  } catch (_) { /* fall through to legacy path */ }

  // Legacy fallback — hidden textarea + execCommand("copy")
  try {
    const ta = document.createElement("textarea");
    ta.value = s;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    ta.style.pointerEvents = "none";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, s.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (_) {
    return false;
  }
}

export async function copyLink(url) {
  const ok = await robustCopy(url);
  if (ok) toast.success("Link copied.");
  else toast.error("Could not copy — please copy the link manually.");
  return ok;
}

/**
 * Native Web Share API with graceful fallback to clipboard.
 * Used by the generic SHARE button — NOT by the Instagram button on desktop.
 */
export async function nativeShareOrCopy({ url, text, title, files }) {
  const payload = { url, text, title };
  if (typeof navigator !== "undefined" && navigator.share) {
    try {
      if (files && files.length && navigator.canShare && navigator.canShare({ files })) {
        payload.files = files;
      }
      await navigator.share(payload);
      return { via: "native" };
    } catch (_) {
      return { via: "cancelled" };
    }
  }
  const ok = await robustCopy(`${text}\n\n${url}`);
  if (ok) toast.success("Copied. Paste to share.");
  else toast.error("Could not copy — please copy the link manually.");
  return { via: ok ? "clipboard" : "failed" };
}

/**
 * Instagram share:
 *  - Mobile: open the native OS share sheet — Instagram appears among share
 *    targets on iOS/Android when the app is installed. This is the ONLY
 *    supported Instagram flow on mobile web today.
 *  - Desktop: NEVER open the OS/browser share sheet (confusing UX; user asked
 *    for a dedicated flow). Caller handles the desktop path via a VOIHU-owned
 *    "Ready for Instagram" panel — this function only signals the mode.
 * Returns { mode: "native" | "desktop-panel" }.
 */
export function pickInstagramMode() {
  if (isMobileUA() && typeof navigator !== "undefined" && navigator.share) {
    return { mode: "native" };
  }
  return { mode: "desktop-panel" };
}

export async function shareInstagramNative({ url, text, title }) {
  if (typeof navigator === "undefined" || !navigator.share) return { via: "unsupported" };
  try {
    await navigator.share({ title, text, url });
    return { via: "native" };
  } catch (_) {
    return { via: "cancelled" };
  }
}

export async function copyForInstagram({ url, text }) {
  const ok = await robustCopy(`${text}\n\n${url}`);
  if (ok) {
    toast.success("Copied. Paste it into your Instagram post, story or DM.");
  } else {
    toast.error("Could not copy — please copy the text manually.");
  }
  return ok;
}

/**
 * Copy the full VOIHU share body + referral URL. Used by the
 * "Copy message + link" button. Toast wording is explicit so the user
 * knows both pieces were captured.
 */
export async function copyMessageAndLink({ url, text }) {
  const ok = await robustCopy(`${text}\n\n${url}`);
  if (ok) toast.success("Message and link copied.");
  else toast.error("Could not copy — please copy the link manually.");
  return ok;
}

/**
 * Facebook / LinkedIn helper: the share URLs do NOT accept prefilled body
 * text (platform-imposed). To keep the sharing UX useful we:
 *   1. Fire the clipboard write BEFORE window.open — copies the VOIHU
 *      share text (URL is intentionally NOT included, the Facebook/LinkedIn
 *      composer already carries the referral URL as the link preview).
 *   2. Immediately open the platform's composer with the referral URL.
 *   3. Show a non-blocking toast telling the user to paste.
 * The window.open call is fire-and-forget-synchronous so popup blockers
 * do not intercept the click. Clipboard failure never prevents opening.
 */
export function shareWithCopyThenOpen({ platform, url, text }) {
  // Kick clipboard write off the click-event tick — must not await.
  robustCopy(text).then((ok) => {
    if (ok) {
      const label = platform === "linkedin" ? "LinkedIn" : "Facebook";
      toast.success(`Text copied — paste it into your ${label} post.`);
    }
  }).catch(() => {});

  const builder = CHANNEL_URLS[platform];
  const opened = builder ? openShareUrl(builder(url, text)) : false;
  return opened;
}
